import './TakeYourBusinessFurther.scss';

import React from 'react';

import {
  Banner,
  BannerDescription,
  BannerImage,
  BannerImageContainer,
  BannerLinks,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Button,
  Carousel,
  CarouselSlide,
  Column,
  FluidImage,
  GridContainer,
  ProgressiveImage,
  Row,
  useScreenClass,
} from '@vp/swan';

import {
  FeatureGroupModel,
  HeroModel,
} from '@vp/digital-common-data-model-lib';

import {
  GET_STARTED_CTA_DETAIL,
  TAKE_YOUR_BUSINESS_FURTHER__DATA_SECTION_LABEL,
} from '../WixWebsitesAnalytics';
import MissingContentAlert from '../../common/MissingContentAlert';

interface TakeYourBusinessFurtherProps {
  content: HeroModel;
  locale: string;
  vistaWixDomainsCtaClick: Function;
  logoMakerCtaClick: Function;
  vistaWixPlusCtaClick: Function;
  accessibility: FeatureGroupModel;
}

interface BannerModel {
  feature: FeatureGroupModel;
  ctaClick: Function;
  isEnabled: boolean;
}

const LOGO_MAKER_BANNER_ENABLED_LOCALES: string[] = [
  'en-us',
  'en-ie',
  'en-ca',
  'fr-ca',
  'en-au',
  'en-nz',
  'en-sg',
  'en-gb',
  'de-at',
  'de-ch',
  'de-de',
  'fr-ch',
  'fr-fr',
];

const VISTA_WIX_PLUS_BANNER_ENABLED_LOCALES: string[] = [];

const TakeYourBusinessFurther = ({
  content,
  locale,
  vistaWixDomainsCtaClick,
  logoMakerCtaClick,
  vistaWixPlusCtaClick,
  accessibility,
}: TakeYourBusinessFurtherProps) => {
  const isMobile = useScreenClass() === 'xs';
  const isSmall = useScreenClass() === 'sm';
  if (!content) {
    return <MissingContentAlert />;
  }

  const ctasHandlers: Function[] = [
    () => vistaWixDomainsCtaClick(),
    () => logoMakerCtaClick(),
    () => vistaWixPlusCtaClick(),
  ];

  const isBannersEnabled: boolean[] = [
    true,
    LOGO_MAKER_BANNER_ENABLED_LOCALES.includes(locale),
    VISTA_WIX_PLUS_BANNER_ENABLED_LOCALES.includes(locale),
  ];

  const banners: Array<BannerModel> = content?.heroMetadata
    ?.map((feature: FeatureGroupModel, index: number) => ({
      feature: feature,
      ctaClick: ctasHandlers[index],
      isEnabled: isBannersEnabled[index],
    }))
    .filter((banner: any) => banner.isEnabled);

  return (
    <GridContainer>
      <Row>
        <Column span={12} px={0}>
          <Carousel
            initialSlide={0}
            mb={0}
            pb={{ xs: 5, sm: 5, md: 5 }}
            slidesToShow={1}
            slidesToScroll={1}
            progressIndicator="dots"
            arrows={!isMobile && !isSmall && banners?.length > 1}
            accessibleTextForDots={
              banners.length > 0
                ? banners.map(
                    (banner, index) =>
                      accessibility.features[5]?.title.replace(
                        '{{SLIDE_NUMBER}}',
                        String(index + 1)
                      ) ?? ''
                  )
                : []
            }
            accessibleTextForVisibleSlides={
              accessibility.features[2]?.hoverText
            }
            accessibleTextForSlide={accessibility.features[2]?.altText}
            accessibleTextForNext={accessibility.features[4].title}
            accessibleTextForPrevious={accessibility.features[3].title}
            aria-roledescription={accessibility.features[0].title}
            aria-label={content.title}
            className="wix-websites-pdp__take-your-business-further__carousel"
          >
            {banners.map((banner: BannerModel, index: number) => (
              <CarouselSlide
                aria-roledescription={accessibility.features[1].title}
                key={`cardSlide_${index + 1}`}
              >
                <Banner
                  variant="card"
                  backgroundColor="standard"
                  textBoxHorizontalAlign="right"
                >
                  <BannerTextContainer>
                    <BannerText>
                      <BannerTitle as={'h2'}>
                        {banner.feature.title}
                      </BannerTitle>

                      <BannerDescription>
                        {banner.feature.description}
                      </BannerDescription>

                      <BannerLinks>
                        <Button
                          skin="primary"
                          data-position="10"
                          data-section={
                            TAKE_YOUR_BUSINESS_FURTHER__DATA_SECTION_LABEL
                          }
                          data-translation={GET_STARTED_CTA_DETAIL}
                          onClick={() => banner.ctaClick()}
                        >
                          {banner.feature?.features?.[0]?.title}
                        </Button>
                      </BannerLinks>
                    </BannerText>
                  </BannerTextContainer>

                  <BannerImageContainer>
                    <BannerImage>
                      <ProgressiveImage
                        placeholder={
                          banner.feature.mediaLinks[0]?.placeholderUrl
                        }
                        srcSet={banner.feature.mediaLinks[0]?.resourceSetUrl}
                        src={banner.feature.mediaLinks[0]?.secureUrl}
                        sizes="(max-width: 2464px) 50vw, 1232px"
                      >
                        {(image) => (
                          <FluidImage
                            alt={banner.feature.media[0].altText}
                            srcSet={image.srcSet}
                            src={image.src}
                            sizes={image.sizes}
                            className="wix-websites-pdp__take-your-business-further__feature-image"
                          />
                        )}
                      </ProgressiveImage>
                    </BannerImage>
                  </BannerImageContainer>
                </Banner>
              </CarouselSlide>
            ))}
          </Carousel>
        </Column>
      </Row>
    </GridContainer>
  );
};

export default TakeYourBusinessFurther;
